import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import "./surveyLogin.css";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Alert,
  Stack,
  Link,
} from '@mui/material';
import { requestExternalAccess, verifyAccessOtp } from "../../services/auth";
import { CRS_EXT_TOKEN, CRS_EXT_PARAM } from "../../utils/constants";

const STEPS = {
  EMAIL: 'email',
  OTP: 'otp'
};

export default function SurveyLogin(props) {
  useEffect(() => {
    props.setShowHeaderFooter(false);
  }, []);
  const history = useHistory();
  const queryString = window.location.search;
  const queryParams = new URLSearchParams(queryString);
  const decryptedParam = queryParams.get("rn");
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [redirectData, setRedirectData] = useState(null);  

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestExternalAccess(email, decryptedParam);
      setStep(STEPS.OTP);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await verifyAccessOtp(email, otp, decryptedParam);
      sessionStorage.setItem(CRS_EXT_TOKEN, data.token);
      sessionStorage.setItem(CRS_EXT_PARAM, decryptedParam);
      // Trigger redirect via state
      setRedirectData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setOtp('');
    setLoading(true);
    try {
      await requestExternalAccess(email, decryptedParam);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (redirectData) {
      console.log('Performing redirect to:', redirectData.pathname);
      history.replace({
        pathname: redirectData.pathname,
        state: redirectData,
      });
    }
  }, [redirectData, history]);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="grey.100"
      p={2}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Typography variant="h1" gutterBottom>
          Request Access
        </Typography>

        {step === STEPS.EMAIL && (
          <>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Enter the email address where you received the link to this login page in order to receive a one-time passcode.
            </Typography>
            <Box component="form" onSubmit={handleRequestAccess} noValidate>
              <Stack spacing={2}>
                <TextField
                  id="email"
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  fullWidth
                  autoComplete="email"
                  disabled={loading}
                  size="medium"
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || !email.trim()}
                  size="large"
                  startIcon={loading && <CircularProgress size={18} color="inherit" />}
                >
                  {loading ? 'Sending…' : 'Send OTP'}
                </Button>
              </Stack>
            </Box>
          </>
        )}

        {step === STEPS.OTP && (
          <>
            <Typography variant="body2" color="text.secondary" mb={3}>
              A one-time passcode was sent to <strong>{email}</strong>. Enter it below.
            </Typography>
            <Box component="form" onSubmit={handleVerifyOtp} noValidate>
              <Stack spacing={2}>
                <TextField
                  id="otp"
                  label="One-time passcode"
                  type="text"
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••••"
                  required
                  fullWidth
                  autoComplete="one-time-code"
                  disabled={loading}
                  inputProps={{ maxLength: 8, style: { letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.25rem' } }}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || !otp.trim()}
                  size="large"
                  startIcon={loading && <CircularProgress size={18} color="inherit" />}
                >
                  {loading ? 'Verifying…' : 'Verify & Get Access'}
                </Button>
              </Stack>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Link
                component="button"
                variant="body2"
                onClick={handleResendOtp}
                disabled={loading}
                underline="hover"
              >
                Resend OTP
              </Link>
              <Link
                component="button"
                variant="body2"
                onClick={() => { setStep(STEPS.EMAIL); setError(''); setOtp(''); }}
                disabled={loading}
                underline="hover"
              >
                Change email
              </Link>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}